import {faStar as farStar} from "@fortawesome/free-regular-svg-icons";
import {
  faStar as fasStar,
  faStarHalfStroke,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import {noop, times} from "lodash";
import {useEffect, useState} from "react";

import classes from "./StarRating.module.scss";

export interface RatingProps {
  rating?: number; // Default rating value
  maxRating?: number; // Max possible rating
  readOnly?: boolean; // Is the rating read-only?
  onChange?(rating?: number): void; // Callback to invoke when the rating value changes
}

/**
 * Star rating
 */
export default function StarRating(props: RatingProps) {
  const {
    rating: defaultRating,
    maxRating = 5,
    readOnly = false,
    onChange = noop,
  } = props;

  const [rating, setRating] = useState<number | undefined>(defaultRating);

  useEffect(() => {
    if (defaultRating !== rating) {
      onChange(rating);
    }
  }, [rating]);

  return (
    <div className={classes.rating}>
      {times(maxRating, i => {
        const currRating = i + 1;

        let title: string | undefined;
        if (readOnly) {
          title = `Rated ${rating?.toFixed(1)} out of ${maxRating}`;
        } else if (rating === currRating) {
          title = "Clear rating";
        } else if (rating) {
          title = `Change rating from ${rating} to ${currRating}`;
        } else {
          title = `Set rating to ${currRating}`;
        }

        let star = farStar; // Empty star
        let starSelected = false;
        if (rating) {
          if (rating >= currRating) {
            star = fasStar; // Full star
            starSelected = true;
          } else if (currRating - rating == 0.5) {
            star = faStarHalfStroke; // Half star
            starSelected = true;
          }
        }

        return (
          <span
            key={i}
            title={title}
            role="radio"
            aria-checked={starSelected}
            aria-disabled={readOnly}
            className={classNames(classes.star, {
              [classes.selected]: starSelected,
              [classes.editable]: !readOnly,
            })}
            onClick={() => {
              if (readOnly) {
                return;
              } else if (currRating === rating) {
                // Clear rating
                setRating(undefined);
              } else {
                setRating(currRating);
              }
            }}>
            <FontAwesomeIcon icon={star} />
          </span>
        );
      })}
    </div>
  );
}
